import { Link } from 'react-router-dom';
import type { comics as ComicsType } from '@/mocks/comics';

type Comic = typeof ComicsType[number];

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  if (variant === 'compact') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group block cursor-pointer"
      >
        <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
          <img
            src={comic.cover}
            alt={comic.title}
            title={`${comic.title} - 飞飞漫画网`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          {comic.status === '已完结' && (
            <span className="absolute top-2 left-2 bg-secondary-500 text-white text-xs px-2 py-0.5 rounded-md">
              完结
            </span>
          )}
          <span className="absolute top-2 right-2 bg-primary-500 text-white text-xs px-2 py-0.5 rounded-md">
            {comic.rating}
          </span>
        </div>
        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500">
          {comic.author} · 第{comic.chapters}话
        </p>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group relative block overflow-hidden rounded-lg aspect-[16/11] cursor-pointer"
      >
        <img
          src={comic.cover}
          alt={comic.title}
          title={`${comic.title} - 飞飞漫画网`}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-2">
            {comic.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">
                {tag}
              </span>
            ))}
          </div>
          <h3 className="text-xl font-bold text-white mb-1">{comic.title}</h3>
          <p className="text-sm text-white/70 line-clamp-2">{comic.description}</p>
        </div>
      </Link>
    );
  }

  // default
  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
    >
      <div className="relative w-20 h-28 flex-shrink-0 overflow-hidden rounded-md">
        <img
          src={comic.cover}
          alt={comic.title}
          title={`${comic.title} - 飞飞漫画网`}
          className="w-full h-full object-cover object-top"
          loading="lazy"
        />
        {comic.status === '已完结' && (
          <span className="absolute top-1 left-1 bg-secondary-500 text-white text-[10px] px-1.5 py-0.5 rounded">
            完结
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mb-1.5">{comic.author}</p>
        <p className="text-xs text-foreground-400 line-clamp-2 leading-relaxed">{comic.description}</p>
        <div className="flex items-center gap-3 mt-2">
          <span className="flex items-center gap-1 text-xs text-primary-500">
            <i className="ri-star-fill text-[10px]"></i>
            {comic.rating}
          </span>
          <span className="text-xs text-foreground-400">{formatViews(comic.views)}次阅读</span>
          <span className="text-xs text-foreground-400">{comic.status}</span>
        </div>
      </div>
    </Link>
  );
}